import { deleteToken, getToken, isSupported } from 'firebase/messaging';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db, messaging } from '../firebase';

const VAPID_KEY = 'BLRtV3UiGEmRUr8PKwycU_5BEleJTQJMF25EFZOiuJ9GFVgkj7ja5ApgQyVV0Q18TLfAItwUOhDGe77a3KS0VRc';
const NOTIFICATION_PROMPT_STORAGE_KEY = 'fantawrestling_notification_prompt_dismissed_at';
const NOTIFICATION_TOKEN_DOC_STORAGE_KEY = 'fantawrestling_notification_token_doc_id';
const NOTIFICATION_PROMPT_COOLDOWN_MS = 1000 * 60 * 60 * 24 * 3;
const FIREBASE_MESSAGING_SW_URL = '/firebase-messaging-sw.js';

export async function isMessagingSupportedInBrowser() {
  if (typeof window === 'undefined') return false;

  try {
    return await isSupported();
  } catch (error) {
    console.warn('Browser non supportato per FCM:', error);
    return false;
  }
}

export function getNotificationPermissionState() {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return 'unsupported';
  }

  return Notification.permission;
}

export function markNotificationPromptDismissed() {
  if (typeof window === 'undefined') return;
  localStorage.setItem(NOTIFICATION_PROMPT_STORAGE_KEY, String(Date.now()));
}

export function clearNotificationPromptDismissed() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(NOTIFICATION_PROMPT_STORAGE_KEY);
}

export function shouldShowNotificationPrompt(user) {
  if (!user || typeof window === 'undefined') return false;

  const permission = getNotificationPermissionState();
  if (permission !== 'default') return false;

  const lastDismissedAt = Number(localStorage.getItem(NOTIFICATION_PROMPT_STORAGE_KEY) || 0);
  return !lastDismissedAt || Date.now() - lastDismissedAt > NOTIFICATION_PROMPT_COOLDOWN_MS;
}

async function waitForActiveServiceWorker(registration) {
  if (registration?.active) {
    return registration;
  }

  const worker = registration?.installing || registration?.waiting;
  if (!worker) {
    throw new Error('Service Worker notifiche non disponibile');
  }

  await new Promise((resolve, reject) => {
    const handleStateChange = () => {
      if (worker.state === 'activated') {
        worker.removeEventListener('statechange', handleStateChange);
        resolve();
      }
    };

    worker.addEventListener('statechange', handleStateChange);
    handleStateChange();

    window.setTimeout(() => {
      worker.removeEventListener('statechange', handleStateChange);
      reject(new Error('Attivazione del Service Worker troppo lenta'));
    }, 10000);
  });

  return registration;
}

export async function ensureMessagingServiceWorkerRegistration() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    throw new Error('Questo browser non supporta i Service Worker');
  }

  let registration = await navigator.serviceWorker.getRegistration(FIREBASE_MESSAGING_SW_URL);

  if (!registration) {
    registration = await navigator.serviceWorker.register(FIREBASE_MESSAGING_SW_URL);
  }

  if (!registration.active) {
    await waitForActiveServiceWorker(registration);
  }

  const readyRegistration = await navigator.serviceWorker.ready;
  return readyRegistration || registration;
}

async function persistNotificationPreferences(user, token, notificationsEnabled) {
  const clientInfo = getNotificationClientInfo();
  const writes = [
    setDoc(
      doc(db, 'users', user.uid),
      {
        fcmToken: token,
        notificationsEnabled,
        notificationsUpdatedAt: serverTimestamp(),
      },
      { merge: true }
    ),
    setDoc(
      doc(db, 'userTokens', user.uid),
      {
        uid: user.uid,
        email: user.email || null,
        token,
        notificationsEnabled,
        ...clientInfo,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    ),
  ];

  if (token) {
    const tokenDocId = getNotificationTokenDocumentId(user, token);
    writes.push(
      setDoc(
        doc(db, 'userTokens', tokenDocId),
        {
          uid: user.uid,
          email: user.email || null,
          token,
          notificationsEnabled,
          ...clientInfo,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      )
    );

    if (typeof window !== 'undefined') {
      localStorage.setItem(NOTIFICATION_TOKEN_DOC_STORAGE_KEY, tokenDocId);
    }
  } else if (typeof window !== 'undefined') {
    const previousTokenDocId = localStorage.getItem(NOTIFICATION_TOKEN_DOC_STORAGE_KEY);

    if (previousTokenDocId) {
      writes.push(
        setDoc(
          doc(db, 'userTokens', previousTokenDocId),
          {
            uid: user.uid,
            email: user.email || null,
            token: null,
            notificationsEnabled: false,
            ...clientInfo,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        )
      );
      localStorage.removeItem(NOTIFICATION_TOKEN_DOC_STORAGE_KEY);
    }
  }

  await Promise.all(writes);
}

function isStandaloneAppDisplay() {
  if (typeof window === 'undefined') return false;

  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator?.standalone === true
  );
}

function getNotificationClientInfo() {
  const isStandaloneApp = isStandaloneAppDisplay();

  return {
    clientType: isStandaloneApp ? 'app' : 'browser',
    isStandaloneApp,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
}

function getNotificationTokenDocumentId(user, token) {
  const encodedToken = window
    .btoa(token)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');

  return `${user.uid}_${encodedToken.slice(0, 120)}`;
}

export async function enablePushNotifications({
  user = auth.currentUser,
  skipPermissionRequest = false,
} = {}) {
  if (!user) {
    throw new Error('Utente non autenticato');
  }

  const supported = await isMessagingSupportedInBrowser();
  if (!supported) {
    throw new Error('Questo browser non supporta le notifiche push');
  }

  const currentPermission = getNotificationPermissionState();
  if (currentPermission === 'denied') {
    throw new Error('Le notifiche sono bloccate nel browser. Riattivale dalle impostazioni del sito.');
  }

  let permission = currentPermission;
  if (!skipPermissionRequest && currentPermission !== 'granted') {
    permission = await Notification.requestPermission();
  }

  if (permission !== 'granted') {
    return {
      enabled: false,
      reason: 'permission-denied',
    };
  }

  let serviceWorkerRegistration;
  try {
    serviceWorkerRegistration = await ensureMessagingServiceWorkerRegistration();
  } catch (error) {
    throw new Error('Service Worker notifiche non ancora attivo. Chiudi e riapri la pagina, poi riprova.');
  }

  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration,
  });
  if (!token) {
    throw new Error('Token notifiche non disponibile');
  }

  await persistNotificationPreferences(user, token, true);
  clearNotificationPromptDismissed();

  return {
    enabled: true,
    token,
  };
}

export async function disablePushNotifications({ user = auth.currentUser } = {}) {
  if (!user) {
    throw new Error('Utente non autenticato');
  }

  const supported = await isMessagingSupportedInBrowser();
  if (supported) {
    try {
      await deleteToken(messaging);
    } catch (error) {
      console.warn('Token locale non eliminato:', error);
    }
  }

  await persistNotificationPreferences(user, null, false);
  markNotificationPromptDismissed();

  return {
    disabled: true,
  };
}

export function buildAbsoluteNotificationLink(link) {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://fantawwe-83b60.web.app';

  if (!link) return `${origin}/news`;
  if (/^https?:\/\//i.test(link)) return link;
  if (link.startsWith('/')) return `${origin}${link}`;

  return `${origin}/${link}`;
}
